import styled from '@emotion/styled';
import { BucketId, FileReference } from '@kaizenplatform/belfast-entity';
import { Paper, Typography } from '@material-ui/core';
import { createElement, FC, Fragment } from 'react';
import { FormattedMessage } from 'react-intl';

import { ProjectDetailsForShowFragment } from '../../../generated/graphql';
import { ProjectForBrief } from '../../../graphql/project';
import theme from '../../../theme';
import CreationSetList from '../../component/compound/CreationSetList';
import CreativeBriefDetail from '../../component/compound/CreativeBriefDetail';
import DummyCreationSetList from '../../component/compound/DummyCreationSetList';
import DummyCreativeBriefDetail from '../../component/compound/DummyCreativeBriefDetail';
import DummyProjectTypeDetail from '../../component/compound/DummyProjectTypeDetail';
import DummyWideContent from '../../component/compound/DummyWideContent';
import PricingMerchandiseOptionsList from '../../component/compound/PricingMerchandiseOptionsList';
import ProjectBasicSettingDetail from '../../component/compound/ProjectBasicSettingDetail';
import ProjectIssue from '../../component/compound/ProjectIssue';
import ProjectTypeDetail from '../../component/compound/ProjectTypeDetail';
import { SPACING_LARGE, SPACING_REGULAR, SPACING_UNIT } from '../../constant/spacing';

interface Props {
  project: (ProjectForBrief & ProjectDetailsForShowFragment) | null;
  briefFiles: Map<BucketId, FileReference[]>;
  assetFiles: ReadonlyArray<FileReference>;
  basicProjectTypeIdByJp: string;
  basicProjectTypeIdByUs: string;
  loading?: boolean;
  className?: string;
  onSubmitProjectIssue: (projectId: string, inputComment: string) => Promise<void>;
  onSubmitProjectIssueComment: (issueId: string, comment: string) => void;
  onUpdateProjectIssueComment: (commentId: string, comment: string) => void;
  onDeleteProjectIssueComment: (commentId: string) => void;
}

const Brief: FC<Props> = ({
  project,
  assetFiles,
  briefFiles,
  basicProjectTypeIdByJp,
  basicProjectTypeIdByUs,
  loading = false,
  className,
  onSubmitProjectIssue,
  onSubmitProjectIssueComment,
  onUpdateProjectIssueComment,
  onDeleteProjectIssueComment,
}) => {
  const projectTypeDetail =
    project && !loading ? <ProjectTypeDetail projectType={project.projectType} /> : <DummyProjectTypeDetail />;

  const creativeBriefDetail =
    project && !loading ? (
      <CreativeBriefDetail
        definition={project.creativeBriefDefinition}
        value={project.creativeBriefValue.values}
        files={briefFiles}
      />
    ) : (
      <DummyCreativeBriefDetail />
    );

  const filteredCreationSet = project?.creationSet?.creativeFormats.every(
    (format) => format.network.key !== 'any_network', // これは何をしてる？
  )
    ? project.creationSet
    : null;

  const creationSetList =
    project && !loading ? (
      filteredCreationSet ? (
        <CreationSetList
          creationSet={filteredCreationSet}
          isPointRequired={false}
          showCreationSetTitle={project.projectType.version <= 1}
        />
      ) : null
    ) : (
      <DummyCreationSetList />
    );

  const pricingMerchandiseOptionsList =
    project && !loading ? <PricingMerchandiseOptionsList project={project} /> : <DummyWideContent />;

  return (
    <div className={className}>
      {filteredCreationSet && (
        <Section>
          <Heading>
            <FormattedMessage id="show_project_page.heading.creative_format" />
          </Heading>
          <ContentPaper>{creationSetList}</ContentPaper>
        </Section>
      )}

      <Section>
        <Heading>
          <FormattedMessage id="show_project_page.heading.product_options" />
        </Heading>

        <ContentPaper>{pricingMerchandiseOptionsList}</ContentPaper>
      </Section>

      <Section>
        <Heading>
          <FormattedMessage id="show_project_page.heading.matching_format" />
        </Heading>

        <ContentPaper>
          {!loading && project?.matchingFormat ? (
            <>
              <Typography variant="body1">
                <FormattedMessage id={`matching_format.${project?.matchingFormat}.label`} />
              </Typography>
              <Typography variant="caption">
                <FormattedMessage id={`matching_format.${project?.matchingFormat}.description`} />
              </Typography>
            </>
          ) : (
            <DummyWideContent />
          )}
        </ContentPaper>
      </Section>

      {!loading && project && (
        <Section>
          <Heading>
            <FormattedMessage id="show_project_page.heading.basic_setting" />
          </Heading>

          <ContentPaper>
            <ProjectBasicSettingDetail
              assetCategoryValue={project.assetCategoryValue || null}
              assetCategorySections={
                project.assetCategoryDefinition != null ? project.assetCategoryDefinition.sections : null
              }
              assetFiles={assetFiles}
            />
          </ContentPaper>
        </Section>
      )}

      {project &&
      project.projectType.id !== basicProjectTypeIdByJp &&
      project.projectType.id !== basicProjectTypeIdByUs ? (
        // Basic Project Type の場合タイトルは表示しない
        <Section>
          <Heading>
            <FormattedMessage id="show_project_page.heading.creative_type" />
          </Heading>

          <ContentPaper>{projectTypeDetail}</ContentPaper>
        </Section>
      ) : null}

      <Section>
        <Heading>
          <FormattedMessage id="show_project_page.heading.creative_brief" />
          {project?.hasChanges ? (
            // projectChangeの存在確認 => projectChange.idを渡す
            <DiffTitle href={`/projects/${project.id}/diff`}>
              <FormattedMessage id="show_project_page.heading.creative_brief_diff" />
            </DiffTitle>
          ) : null}
        </Heading>

        <ContentPaper>{creativeBriefDetail}</ContentPaper>
      </Section>

      {project ? (
        <Section>
          <ProjectIssue
            project={project}
            onSubmitProjectIssue={onSubmitProjectIssue}
            onSubmitProjectIssueComment={onSubmitProjectIssueComment}
            onUpdateProjectIssueComment={onUpdateProjectIssueComment}
            onDeleteProjectIssueComment={onDeleteProjectIssueComment}
          />
        </Section>
      ) : null}
    </div>
  );
};

const Heading = styled(Typography)({
  marginBottom: SPACING_REGULAR,
  display: 'flex',
  justifyContent: 'space-between',
});
Heading.defaultProps = { variant: 'h5', color: 'textSecondary' };

const ContentPaper = styled(Paper)({
  padding: SPACING_REGULAR,
});

const Section = styled('div')({
  marginBottom: SPACING_LARGE,
});

const DiffTitle = styled('a')({
  wordWrap: 'break-word',
  wordBreak: 'break-word' as any,
  marginBottom: SPACING_UNIT,
  fontSize: 16,
  fontWeight: 500,
  lineHeight: '24px',
  fontFamily: "'Roboto', 'Helvetica', 'Arial', 'sans-serif'",
  color: theme.palette.primary.main,
  textDecoration: 'none',
  cursor: 'pointer',
  '&:hover': {
    color: theme.palette.primary.main,
    textDecoration: 'underline',
  },
  '&:active': {
    color: theme.palette.primary.main,
  },
  '&:visited': {
    color: theme.palette.primary.main,
  },
});

export default Brief;
